import type { StartConfig } from 'owa-shared-start';
import { updateServiceConfig } from 'owa-service/lib/config';
import {
    lazyInitializeMetaOsAppSdkProxy,
    lazyGetOwaAuthTokenFromMetaOsHubProxy,
    lazyGetOwaDecodedAuthTokenFromMetaOsHubProxy,
    lazyInitializeMetaOsCapabilitiesProxy,
    lazyGetContextProxy,
    lazyPreInitOwaAccountSourceListStoreProxy,
    lazyGetAccountServiceConfigProxy,
    lazyIsNAAChannelRecommendedProxy,
} from 'owa-metaos-app-bootstrap/lib/lazyAppBoot';
import { setHostHub } from 'owa-config';
import { addBootCustomData, markFunction } from 'owa-performance';
import { isMsalFlowEnabled } from 'owa-msaljs/lib/isMsalFlowEnabled';

export const getRunBeforeStartForHosted = (config: StartConfig) => {
    const oldRunBeforeStart = config.runBeforeStart;

    return (startConfig: StartConfig): Promise<void | undefined> =>
        markFunction(lazyInitializeMetaOsAppSdkProxy.import, 'MetaOSAppBootImportScript')().then(
            async initializeMetaOsAppSdk => {
                // must finish before any other Hub SDK operations
                await initializeMetaOsAppSdk();

                const {
                    app: {
                        host: {
                            name: metaOsHostName,
                            clientType: metaOsHostClientType,
                            sessionId: metaOsHostSessionId,
                        },
                        locale: metaOsLocale,
                        userClickTime,
                    },
                    user: metaOsUser,
                } = await lazyGetContextProxy.importAndExecute();

                // ensure this is set early before any calls that would depend on host info for eg. flighting
                setHostHub(metaOsHostName, metaOsHostClientType);

                const getAuthTokenEnabled =
                    !isMsalFlowEnabled() ||
                    (!startConfig.msalConfiguration?.disableGetAuthTokenFallback &&
                        !(await lazyIsNAAChannelRecommendedProxy.importAndExecute()));

                if (getAuthTokenEnabled) {
                    // if using MSAL is disabled, or the host indicates it does not support NAA, override the
                    // getAuthToken callback with the MetaOS Hub SDK getAuthToken callback
                    await lazyGetOwaAuthTokenFromMetaOsHubProxy
                        .import()
                        .then(getAuthToken => updateServiceConfig({ getAuthToken }));
                }

                lazyInitializeMetaOsCapabilitiesProxy.importAndExecute();
                lazyIsNAAChannelRecommendedProxy.importAndExecute().then(isRecommended => {
                    updateServiceConfig({ isNAAChannelRecommended: isRecommended });
                });

                if (metaOsHostSessionId) {
                    addBootCustomData('HostSessionId', metaOsHostSessionId);
                }
                if (userClickTime) {
                    addBootCustomData('HubLoadData', { userClickTime });
                }

                if (config.runAfterMetaOsAppInitialized) {
                    config.runAfterMetaOsAppInitialized(
                        metaOsHostName,
                        metaOsHostClientType,
                        metaOsUser?.id,
                        metaOsUser?.tenant?.id,
                        metaOsHostSessionId
                    );
                }

                updateServiceConfig({
                    metaOSContext: {
                        locale: metaOsLocale || 'en-us',
                        hostName: metaOsHostName,
                        clientType: metaOsHostClientType,
                    },
                });

                // Initialize the account source list store for Calendar, Places, and Bookings metaos app using PUID
                const hostedApp = config.app;
                if (
                    (hostedApp === 'Calendar' ||
                        hostedApp === 'Places' ||
                        hostedApp === 'CalendarDeepLink' ||
                        hostedApp === 'Bookings') &&
                    getAuthTokenEnabled
                ) {
                    const decodedToken =
                        await lazyGetOwaDecodedAuthTokenFromMetaOsHubProxy.importAndExecute();
                    // Cleanup once MetaOS begins to transmit PUID https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/253607
                    if (decodedToken?.puid) {
                        await markFunction(
                            lazyPreInitOwaAccountSourceListStoreProxy.importAndExecute,
                            'MetaOSInitAccountStore'
                        )(
                            decodedToken.puid,
                            metaOsUser?.tenant?.id,
                            metaOsUser?.loginHint,
                            metaOsUser?.userPrincipalName
                        );
                        updateServiceConfig(
                            await lazyGetAccountServiceConfigProxy.importAndExecute()
                        );
                    }
                }

                return oldRunBeforeStart?.(startConfig);
            }
        );
};
